import React, { useEffect, useState, Suspense, lazy } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppProvider, useApp } from './context/AppContext';
import { Layout } from './components/layout/Layout';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { Permissions } from './types';
// ── Lazy-loaded pages (code splitting) ──────────────────────────────────────
const NewVisitPage = lazy(() => import('./pages/NewVisitPage').then(m => ({
  default: m.NewVisitPage
})));
const VisitsPage = lazy(() => import('./pages/VisitsPage').then(m => ({
  default: m.VisitsPage
})));
const CollectionsPage = lazy(() => import('./pages/CollectionsPage').then(m => ({
  default: m.CollectionsPage
})));
const SalesPage = lazy(() => import('./pages/SalesPage').then(m => ({
  default: m.SalesPage
})));
const ReportsPage = lazy(() => import('./pages/ReportsPage').then(m => ({
  default: m.ReportsPage
})));
const UsersPage = lazy(() => import('./pages/UsersPage').then(m => ({
  default: m.UsersPage
})));
const RolesPage = lazy(() => import('./pages/RolesPage').then(m => ({
  default: m.RolesPage
})));
const MapPage = lazy(() => import('./pages/MapPage').then(m => ({
  default: m.MapPage
})));
const ClientsPage = lazy(() => import('./pages/ClientsPage').then(m => ({
  default: m.ClientsPage
})));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({
  default: m.SettingsPage
})));
const BitacoraPage = lazy(() => import('./pages/BitacoraPage').then(m => ({
  default: m.BitacoraPage
})));
const CuentasCobrarPage = lazy(() => import('./pages/CuentasCobrarPage').then(m => ({
  default: m.CuentasCobrarPage
})));
const PedidosPage = lazy(() => import('./pages/PedidosPage').then(m => ({
  default: m.PedidosPage
})));
const InventarioPage = lazy(() => import('./pages/InventarioPage').then(m => ({
  default: m.InventarioPage
})));
const CajaVaciaPage = lazy(() => import('./pages/CajaVaciaPage').then(m => ({
  default: m.CajaVaciaPage
})));
const PlanificacionPage = lazy(() => import('./pages/PlanificacionPage').then(m => ({
  default: m.PlanificacionPage
})));
const FacturasDejadasPage = lazy(() => import('./pages/FacturasDejadasPage').then(m => ({
  default: m.FacturasDejadasPage
})));
const DevolucionesPage = lazy(() => import('./pages/DevolucionesPage').then(m => ({
  default: m.DevolucionesPage
})));
const OfertasPage = lazy(() => import('./pages/OfertasPage').then(m => ({
  default: m.OfertasPage
})));
const ComisionesPage = lazy(() => import('./pages/ComisionesPage').then(m => ({
  default: m.ComisionesPage
})));
const ConducesPage = lazy(() => import('./pages/ConducesPage').then(m => ({
  default: m.ConducesPage
})));
const ComprasPage = lazy(() => import('./pages/ComprasPage').then(m => ({
  default: m.ComprasPage
})));
const CallCenterPage = lazy(() => import('./pages/CallCenterPage').then(m => ({
  default: m.CallCenterPage
})));
type Page = 'dashboard' | 'visits' | 'new-visit' | 'collections' | 'sales' | 'reports' | 'users' | 'roles' | 'map' | 'clients' | 'settings' | 'bitacora' | 'cuentas-cobrar' | 'pedidos' | 'inventario' | 'caja-vacia' | 'planificacion' | 'facturas-dejadas' | 'devoluciones' | 'ofertas' | 'comisiones' | 'conduces' | 'compras' | 'call-center';
const pageConfig: Record<Page, {
  title: string;
  subtitle: string;
  permission: keyof Permissions | 'settings';
}> = {
  dashboard: {
    title: 'Panel Principal',
    subtitle: 'Resumen de actividad',
    permission: 'dashboard'
  },
  visits: {
    title: 'Mis Visitas',
    subtitle: 'Historial de visitas realizadas',
    permission: 'visits'
  },
  'new-visit': {
    title: 'Nueva Visita',
    subtitle: 'Registrar visita a cliente',
    permission: 'new-visit'
  },
  collections: {
    title: 'Cobros',
    subtitle: 'Gestión de cobros y pagos',
    permission: 'collections'
  },
  sales: {
    title: 'Ventas',
    subtitle: 'Registro de ventas realizadas',
    permission: 'sales'
  },
  clients: {
    title: 'Farmacias',
    subtitle: 'Gestión de farmacias y ubicaciones',
    permission: 'clients'
  },
  reports: {
    title: 'Reportes',
    subtitle: 'Informes y estadísticas',
    permission: 'reports'
  },
  users: {
    title: 'Usuarios',
    subtitle: 'Administración de usuarios',
    permission: 'users'
  },
  roles: {
    title: 'Roles y Permisos',
    subtitle: 'Configuración de acceso',
    permission: 'roles'
  },
  map: {
    title: 'Mapa GPS',
    subtitle: 'Ubicación de visitas',
    permission: 'map'
  },
  settings: {
    title: 'Configuración',
    subtitle: 'Perfil, seguridad y notificaciones',
    permission: 'settings'
  },
  bitacora: {
    title: 'Bitácora',
    subtitle: 'Registro de actividad del sistema',
    permission: 'bitacora'
  },
  'cuentas-cobrar': {
    title: 'Cuentas por Cobrar',
    subtitle: 'Gestión de facturas pendientes',
    permission: 'cuentas_cobrar'
  },
  pedidos: {
    title: 'Pedidos',
    subtitle: 'Gestión de órdenes de compra',
    permission: 'pedidos'
  },
  inventario: {
    title: 'Inventario',
    subtitle: 'Control de stock de productos',
    permission: 'inventario'
  },
  'caja-vacia': {
    title: 'Caja Vacía',
    subtitle: 'Compra de cajas vacías',
    permission: 'caja_vacia'
  },
  'facturas-dejadas': {
    title: 'Facturas Dejadas',
    subtitle: 'Control de facturas físicas entregadas',
    permission: 'facturas_dejadas'
  },
  devoluciones: {
    title: 'Devoluciones',
    subtitle: 'Registro de productos devueltos o vencidos',
    permission: 'devoluciones'
  },
  ofertas: {
    title: 'Ofertas e Impulsos',
    subtitle: 'Gestión de productos destacados',
    permission: 'ofertas'
  },
  comisiones: {
    title: 'Comisiones',
    subtitle: 'Gestión de comisiones por venta y cobro',
    permission: 'comisiones'
  },
  conduces: {
    title: 'Conduces',
    subtitle: 'Gestión de notas de entrega y despacho',
    permission: 'conduces'
  },
  compras: {
    title: 'Compras',
    subtitle: 'Ingreso de productos al inventario',
    permission: 'compras'
  },
  planificacion: {
    title: 'Planificación',
    subtitle: 'Gestión comercial semanal',
    permission: 'planificacion'
  },
  'call-center': {
    title: 'Call Center',
    subtitle: 'Speech interactivo y gestión telefónica',
    permission: 'call_center'
  }
};
function AppContent() {
  const {
    isAuthenticated,
    isLoading,
    user
  } = useAuth();
  const {
    isLoading: dataLoading,
    isBootstrapping
  } = useApp();
  const [currentPage, setCurrentPage] = useState<Page>('dashboard');
  const hasPermission = (page: Page) => {
    if (!user) return false;
    if (page === 'settings') return true;
    if (page === 'clients') {
      return user.rolNombre === 'Administrador' || user.permisos?.users === true || user.permisos?.clients === true;
    }
    if (page === 'bitacora') {
      return user.rolNombre === 'Administrador' || user.permisos?.bitacora === true;
    }
    // New modules: require explicit permission (no undefined fallback)
    if (['cuentas-cobrar', 'pedidos', 'inventario', 'caja-vacia', 'facturas-dejadas', 'devoluciones', 'ofertas', 'comisiones', 'conduces', 'compras', 'sales', 'planificacion', 'call-center'].includes(page)) {
      const permKey = pageConfig[page].permission as keyof Permissions;
      return user.rolNombre === 'Administrador' || user.permisos?.[permKey] === true;
    }
    return user.permisos[pageConfig[page].permission as keyof Permissions] === true;
  };
  useEffect(() => {
    if (user && !hasPermission(currentPage)) {
      const firstPermitted = Object.keys(pageConfig).find(key => hasPermission(key as Page)) as Page;
      if (firstPermitted) setCurrentPage(firstPermitted);
    }
  }, [user, currentPage]);
  if (isLoading) {
    return <div className="min-h-screen bg-slate-900 flex items-center justify-center" data-id="element-0">
        <div className="text-center" data-id="element-1">
          <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-2" />
          <p className="text-slate-400 text-sm" data-id="element-3">Cargando...</p>
        </div>
      </div>;
  }
  if (!isAuthenticated) return <LoginPage data-id="element-4" />;
  // Only block the full UI until lightweight tables (clients/users/roles) are ready.
  // Visits continue loading in the background — each page shows its own skeleton.
  if (isBootstrapping) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-id="element-5">
        <div className="text-center" data-id="element-6">
          <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-7" />
          <p className="text-slate-500 text-sm" data-id="element-8">Cargando datos...</p>
        </div>
      </div>;
  }
  const PageLoader = () => <div className="flex items-center justify-center h-[40vh]" data-id="element-9">
      <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin" data-id="element-10" />
    </div>;
  const renderPage = () => {
    if (user && !hasPermission(currentPage)) {
      return <div className="flex flex-col items-center justify-center h-[60vh] text-center" data-id="element-11">
          <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center mb-4" data-id="element-12">
            <svg className="w-8 h-8 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-13">
              
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" data-id="element-14" />
              
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-900" data-id="element-15">Acceso Denegado</h2>
          <p className="text-slate-500 mt-2" data-id="element-16">
            No tienes permisos para ver esta página.
          </p>
        </div>;
    }
    switch (currentPage) {
      case 'dashboard':
        return <DashboardPage data-id="element-17" />;
      case 'visits':
        return <VisitsPage data-id="element-18" />;
      case 'new-visit':
        return <NewVisitPage data-id="element-19" />;
      case 'collections':
        return <CollectionsPage data-id="element-20" />;
      case 'sales':
        return <SalesPage data-id="element-21" />;
      case 'clients':
        return <ClientsPage data-id="element-22" />;
      case 'reports':
        return <ReportsPage data-id="element-23" />;
      case 'users':
        return <UsersPage data-id="element-24" />;
      case 'roles':
        return <RolesPage data-id="element-25" />;
      case 'map':
        return <MapPage data-id="element-26" />;
      case 'settings':
        return <SettingsPage data-id="element-27" />;
      case 'bitacora':
        return <BitacoraPage data-id="element-28" />;
      case 'cuentas-cobrar':
        return <CuentasCobrarPage data-id="element-29" />;
      case 'pedidos':
        return <PedidosPage data-id="element-30" />;
      case 'inventario':
        return <InventarioPage data-id="element-31" />;
      case 'caja-vacia':
        return <CajaVaciaPage data-id="element-32" />;
      case 'facturas-dejadas':
        return <FacturasDejadasPage data-id="element-33" />;
      case 'devoluciones':
        return <DevolucionesPage data-id="element-34" />;
      case 'ofertas':
        return <OfertasPage data-id="element-35" />;
      case 'comisiones':
        return <ComisionesPage data-id="element-36" />;
      case 'conduces':
        return <ConducesPage data-id="element-37" />;
      case 'compras':
        return <ComprasPage data-id="element-38" />;
      case 'planificacion':
        return <PlanificacionPage data-id="element-39" />;
      case 'call-center':
        return <CallCenterPage data-id="element-40" />;
      default:
        return <DashboardPage data-id="element-41" />;
    }
  };
  const config = pageConfig[currentPage];
  return <Layout currentPage={currentPage} onNavigate={page => setCurrentPage(page as Page)} title={config.title} subtitle={config.subtitle} data-id="element-42">
      
      <Suspense fallback={<PageLoader data-id="element-44" />} data-id="element-43">{renderPage()}</Suspense>
    </Layout>;
}
function NewOrderBanner() {
  const {
    isAuthenticated,
    user
  } = useAuth();
  const {
    newOrderAlert,
    clearNewOrderAlert
  } = useApp();
  if (!isAuthenticated || !newOrderAlert) return null;
  const isRepartidor = user?.permisos?.dashboard_entregas === true && user?.rolNombre !== 'Administrador';
  return <div className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-between gap-4 px-6 py-4 bg-orange-500 text-white shadow-2xl animate-bounce" data-id="element-45">
      <div className="flex items-center gap-3 font-bold text-lg" data-id="element-46">
        <span className="text-2xl" data-id="element-47">{isRepartidor ? '📦' : '🛒'}</span>
        {isRepartidor ? '¡PEDIDO EMPACADO LISTO PARA ENTREGA! — Revisa tus entregas' : '¡NUEVO PEDIDO RECIBIDO! — Revisa el módulo de Pedidos'}
      </div>
      <button onClick={clearNewOrderAlert} className="px-4 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-semibold transition-colors" data-id="element-48">
        
        Cerrar
      </button>
    </div>;
}
export function App() {
  return <AuthProvider data-id="element-49">
      <AppProvider data-id="element-50">
        <NewOrderBanner data-id="element-51" />
        <AppContent data-id="element-52" />
      </AppProvider>
    </AuthProvider>;
}